import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api, getErrorMessage } from '../../lib/api';
import { alert, toast } from '../../utils/toast';
import type { ProductEndpoint, IntegrationRoute } from '../../types';
import { Pause, Play, Plus } from 'lucide-react';

interface EndpointManagementProps {
  productId: string;
  endpoints: ProductEndpoint[];
  onEndpointSelect: (endpoint: ProductEndpoint | null) => void;
  onUpdate: () => void;
}

export const EndpointManagement: React.FC<EndpointManagementProps> = ({
  productId,
  endpoints,
  onEndpointSelect,
  onUpdate,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [editingEndpoint, setEditingEndpoint] = useState<ProductEndpoint | null>(null);
  const [selectedRouteId, setSelectedRouteId] = useState<string>('');

  const [routeSearch, setRouteSearch] = useState('');
  const [showRouteDropdown, setShowRouteDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowRouteDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const { data: routesData } = useQuery({
    queryKey: ['integration-routes-all'],
    queryFn: async () => {
      const response = await api.get<{
        routes: IntegrationRoute[];
      }>(`/admin/integration-routes?page=1&limit=100`);
      return response.data;
    },
  });

  useEffect(() => {
    if (editingEndpoint && routesData?.routes) {
      const matchingRoute = routesData.routes.find(
        (r) =>
          r.endpoint_path === editingEndpoint.public_path &&
          r.http_method === editingEndpoint.public_method
      );
      if (matchingRoute) {
        setSelectedRouteId(matchingRoute.id);
        setRouteSearch(`${matchingRoute.name} (${matchingRoute.endpoint_path})`);
      } else {
        setSelectedRouteId('');
        setRouteSearch('');
      }
    }
  }, [editingEndpoint, routesData?.routes]);

  const [formData, setFormData] = useState<{
    name: string;
    description: string;
    enabled: boolean;
    public_method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
    public_path: string;
    external_method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
    external_path: string;
    timeout_ms: number | undefined;
    retry_count: number | undefined;
    cost_per_request: number;
    rate_limit_per_minute: number | undefined;
    rate_limit_per_day: number | undefined;
  }>({
    name: '',
    description: '',
    enabled: true,
    public_method: 'GET',
    public_path: '',
    external_method: 'GET',
    external_path: '',
    timeout_ms: undefined,
    retry_count: undefined,
    cost_per_request: 0,
    rate_limit_per_minute: undefined,
    rate_limit_per_day: undefined,
  });

  const createMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const response = await api.post(`/portal/admin/products/${productId}/endpoints`, data);
      return response.data;
    },
    onSuccess: () => {
      onUpdate();
      closeModal();
      toast.success('Endpoint criado com sucesso!');
    },
    onError: (error) => {
      toast.error(`Falha ao criar endpoint: ${getErrorMessage(error)}`);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: { id: string } & Partial<typeof formData>) => {
      const { id, ...updateData } = data;
      const response = await api.put(`/portal/admin/endpoints/${id}`, updateData);
      return response.data;
    },
    onSuccess: () => {
      onUpdate();
      closeModal();
      toast.success('Endpoint atualizado com sucesso!');
    },
    onError: (error) => {
      toast.error(`Falha ao atualizar endpoint: ${getErrorMessage(error)}`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/portal/admin/endpoints/${id}`);
    },
    onSuccess: () => {
      onUpdate();
      toast.success('Endpoint eliminado com sucesso!');
    },
    onError: (error) => {
      toast.error(`Falha ao eliminar endpoint: ${getErrorMessage(error)}`);
    },
  });

  const toggleEnabledMutation = useMutation({
    mutationFn: async ({ id, enabled }: { id: string; enabled: boolean }) => {
      await api.put(`/portal/admin/endpoints/${id}`, { enabled });
    },
    onSuccess: () => {
      onUpdate();
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingEndpoint) {
      updateMutation.mutate({ id: editingEndpoint.id, ...formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const filteredRoutes = useMemo(() => {
    if (!routesData?.routes) return [];
    const term = routeSearch.toLowerCase();
    return routesData.routes.filter(
      (r) => r.name.toLowerCase().includes(term) || r.endpoint_path.toLowerCase().includes(term)
    );
  }, [routesData?.routes, routeSearch]);

  const handleRouteSelection = (routeId: string) => {
    setSelectedRouteId(routeId);
    setShowRouteDropdown(false);

    if (!routeId) {
      setRouteSearch('');
      return;
    }

    const route = routesData?.routes.find((r) => r.id === routeId);
    if (!route) return;

    setRouteSearch(`${route.name} (${route.endpoint_path})`);

    let extPath = '';
    try {
      if (route.external_url) {
        const url = new URL(route.external_url);
        extPath = url.pathname;
      }
    } catch {
      extPath = route.external_url || '';
    }

    setFormData((prev) => ({
      ...prev,
      name: prev.name || route.name,
      public_method: route.http_method,
      public_path: route.endpoint_path,
      external_method: route.http_method,
      external_path: extPath || route.endpoint_path,
    }));
  };

  const handleEdit = (endpoint: ProductEndpoint) => {
    setEditingEndpoint(endpoint);

    setFormData({
      name: endpoint.name,
      description: endpoint.description || '',
      enabled: endpoint.enabled,
      public_method: endpoint.public_method,
      public_path: endpoint.public_path,
      external_method: endpoint.external_method,
      external_path: endpoint.external_path,
      timeout_ms: endpoint.timeout_ms,
      retry_count: endpoint.retry_count,
      cost_per_request: endpoint.cost_per_request,
      rate_limit_per_minute: endpoint.rate_limit_per_minute,
      rate_limit_per_day: endpoint.rate_limit_per_day,
    });
    setShowModal(true);
  };

  const handleDelete = async (endpoint: ProductEndpoint) => {
    const result = await alert.confirm(
      'Eliminar Endpoint?',
      `Tem a certeza de que deseja eliminar o endpoint "${endpoint.name}"? Esta ação não pode ser desfeita.`
    );
    if (result.isConfirmed) {
      deleteMutation.mutate(endpoint.id);
    }
  };

  const handleToggleEnabled = (endpoint: ProductEndpoint) => {
    toggleEnabledMutation.mutate({ id: endpoint.id, enabled: !endpoint.enabled });
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingEndpoint(null);
    setSelectedRouteId('');
    setRouteSearch('');
    setFormData({
      name: '',
      description: '',
      enabled: true,
      public_method: 'GET',
      public_path: '',
      external_method: 'GET',
      external_path: '',
      timeout_ms: undefined,
      retry_count: undefined,
      cost_per_request: 0,
      rate_limit_per_minute: undefined,
      rate_limit_per_day: undefined,
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Endpoints</h3>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Gira os endpoints de API para este produto
          </p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="btn-primary inline-flex items-center gap-1.5"
        >
          <Plus className="w-4 h-4" />
          <span>Adicionar Endpoint</span>
        </button>
      </div>

      {endpoints.length > 0 ? (
        <div className="space-y-3">
          {endpoints.map((endpoint) => (
            <div
              key={endpoint.id}
              className="card hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => onEndpointSelect(endpoint)}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="badge badge-info font-mono text-xs">
                      {endpoint.public_method}
                    </span>
                    <h4 className="font-semibold text-slate-900 dark:text-white">
                      {endpoint.name}
                    </h4>
                    <span className={`badge ${endpoint.enabled ? 'badge-success' : 'badge-gray'}`}>
                      {endpoint.enabled ? 'Ativo' : 'Inativo'}
                    </span>
                  </div>

                  <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">
                    {endpoint.description}
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs text-slate-500 dark:text-slate-400">
                    <div>
                      <span className="font-medium">Caminho Público:</span>
                      <code className="ml-1 bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded">
                        {endpoint.public_path}
                      </code>
                    </div>
                    <div>
                      <span className="font-medium">Caminho Externo:</span>
                      <code className="ml-1 bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded">
                        {endpoint.external_method} {endpoint.external_path}
                      </code>
                    </div>
                    {Number(endpoint.cost_per_request) > 0 && (
                      <div>
                        <span className="font-medium">Custo:</span>
                        <span className="ml-1">
                          ${Number(endpoint.cost_per_request).toFixed(4)}/pedido
                        </span>
                      </div>
                    )}
                    {endpoint.rate_limit_per_minute && (
                      <div>
                        <span className="font-medium">Limite Débito:</span>
                        <span className="ml-1">{endpoint.rate_limit_per_minute}/min</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex gap-2 ml-4" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => handleToggleEnabled(endpoint)}
                    disabled={toggleEnabledMutation.isPending}
                    className={`btn-secondary text-sm p-2 ${endpoint.enabled ? '' : 'opacity-50'}`}
                    title={endpoint.enabled ? 'Desativar' : 'Ativar'}
                  >
                    {endpoint.enabled ? (
                      <Pause className="w-4 h-4" />
                    ) : (
                      <Play className="w-4 h-4" />
                    )}
                  </button>
                  <button onClick={() => handleEdit(endpoint)} className="btn-secondary text-sm">
                    Editar
                  </button>
                  <button
                    onClick={() => handleDelete(endpoint)}
                    disabled={deleteMutation.isPending}
                    className="btn-danger text-sm"
                  >
                    Eliminar
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="card text-center py-12">
          <p className="text-slate-600 dark:text-slate-400 mb-4">
            Nenhum endpoint configurado ainda
          </p>
          <button onClick={() => setShowModal(true)} className="btn-primary">
            Adicione o Seu Primeiro Endpoint
          </button>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div
          className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              closeModal();
            }
          }}
        >
          <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl border border-slate-200 dark:border-slate-800">
            <h3 className="text-lg font-semibold mb-4 text-slate-900 dark:text-white">
              {editingEndpoint ? 'Editar Endpoint' : 'Criar Novo Endpoint'}
            </h3>

            <form onSubmit={handleSubmit} className="space-y-4">
              {(createMutation.isError || updateMutation.isError) && (
                <div className="rounded-md bg-red-50 p-4">
                  <p className="text-sm text-red-800">
                    {getErrorMessage(createMutation.error || updateMutation.error)}
                  </p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 relative" ref={dropdownRef}>
                  <label htmlFor="route_search" className="label text-gray-700 font-medium mb-1">
                    Vincular a Rota de Integração (Pesquisar e Preencher Automático)
                  </label>
                  <input
                    id="route_search"
                    type="text"
                    autoComplete="off"
                    placeholder="Pesquisar rota por nome ou caminho..."
                    className="input mb-4 bg-primary-50 border-primary-200 text-primary-900 placeholder:text-primary-400"
                    value={routeSearch}
                    onClick={() => setShowRouteDropdown(true)}
                    onChange={(e) => {
                      setRouteSearch(e.target.value);
                      setShowRouteDropdown(true);
                      if (selectedRouteId) setSelectedRouteId(''); // clear selection if typing
                    }}
                  />

                  {showRouteDropdown && (
                    <div
                      className="absolute z-10 w-full bg-white border border-gray-200 rounded-md shadow-xl max-h-60 overflow-y-auto"
                      style={{ top: '70px' }}
                    >
                      <div
                        className="px-4 py-3 hover:bg-gray-50 cursor-pointer text-gray-500 italic border-b border-gray-100"
                        onClick={() => handleRouteSelection('')}
                      >
                        -- Nenhum / Preenchimento Manual --
                      </div>

                      {filteredRoutes.length > 0 ? (
                        filteredRoutes.map((r) => (
                          <div
                            key={r.id}
                            className={`px-4 py-3 hover:bg-primary-50 cursor-pointer border-b border-gray-50 transition-colors ${selectedRouteId === r.id ? 'bg-primary-50' : ''}`}
                            onClick={() => handleRouteSelection(r.id)}
                          >
                            <div className="font-medium text-gray-900">{r.name}</div>
                            <div className="text-xs text-gray-500 mt-1 font-mono">
                              {r.endpoint_path}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="px-4 py-3 text-sm text-gray-500 text-center">
                          Nenhuma rota encontrada
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="col-span-2">
                  <label htmlFor="name" className="label text-gray-700 font-medium mb-1">
                    Nome do Endpoint *
                  </label>
                  <input
                    id="name"
                    type="text"
                    required
                    className="input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </div>

                <div className="col-span-2">
                  <label htmlFor="description" className="label text-gray-700 font-medium mb-1">
                    Descrição
                  </label>
                  <textarea
                    id="description"
                    className="textarea h-20"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  />
                </div>

                <div>
                  <label htmlFor="public_method" className="label text-gray-700 font-medium mb-1">
                    Método Público *
                  </label>
                  <select
                    id="public_method"
                    required
                    className="select"
                    value={formData.public_method}
                    onChange={(e) =>
                      setFormData({ ...formData, public_method: e.target.value as any })
                    }
                  >
                    <option value="GET">GET</option>
                    <option value="POST">POST</option>
                    <option value="PUT">PUT</option>
                    <option value="DELETE">DELETE</option>
                    <option value="PATCH">PATCH</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="public_path" className="label text-gray-700 font-medium mb-1">
                    Caminho Público *
                  </label>
                  <input
                    id="public_path"
                    type="text"
                    required
                    placeholder="/v1/recurso"
                    className="input"
                    value={formData.public_path}
                    onChange={(e) => setFormData({ ...formData, public_path: e.target.value })}
                  />
                </div>

                <div>
                  <label htmlFor="external_method" className="label text-gray-700 font-medium mb-1">
                    Método Externo *{' '}
                    {selectedRouteId && (
                      <span className="text-xs text-gray-500 font-normal ml-1">
                        (Bloqueado pela Rota)
                      </span>
                    )}
                  </label>
                  <select
                    id="external_method"
                    required
                    disabled={!!selectedRouteId}
                    className={`select ${selectedRouteId ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    value={formData.external_method}
                    onChange={(e) =>
                      setFormData({ ...formData, external_method: e.target.value as any })
                    }
                  >
                    <option value="GET">GET</option>
                    <option value="POST">POST</option>
                    <option value="PUT">PUT</option>
                    <option value="DELETE">DELETE</option>
                    <option value="PATCH">PATCH</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="external_path" className="label text-gray-700 font-medium mb-1">
                    Caminho Externo *{' '}
                    {selectedRouteId && (
                      <span className="text-xs text-gray-500 font-normal ml-1">
                        (Bloqueado pela Rota)
                      </span>
                    )}
                  </label>
                  <input
                    id="external_path"
                    type="text"
                    required
                    disabled={!!selectedRouteId}
                    placeholder="/api/v2/recurso"
                    className={`input ${selectedRouteId ? 'bg-gray-100 cursor-not-allowed text-gray-500' : ''}`}
                    value={formData.external_path}
                    onChange={(e) => setFormData({ ...formData, external_path: e.target.value })}
                  />
                </div>

                <div>
                  <label htmlFor="cost" className="label text-gray-700 font-medium mb-1">
                    Custo por Pedido ($)
                  </label>
                  <input
                    id="cost"
                    type="number"
                    step="0.0001"
                    min="0"
                    className="input"
                    value={formData.cost_per_request}
                    onChange={(e) =>
                      setFormData({ ...formData, cost_per_request: parseFloat(e.target.value) })
                    }
                  />
                </div>

                <div>
                  <label htmlFor="timeout" className="label text-gray-700 font-medium mb-1">
                    Sobreposição de Tempo Limite (ms)
                  </label>
                  <input
                    id="timeout"
                    type="number"
                    min="1000"
                    max="300000"
                    className="input"
                    value={formData.timeout_ms || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        timeout_ms: e.target.value ? parseInt(e.target.value) : undefined,
                      })
                    }
                    placeholder="Herdar do produto"
                  />
                </div>

                <div>
                  <label htmlFor="retry" className="label text-gray-700 font-medium mb-1">
                    Sobreposição de Tentativas
                  </label>
                  <input
                    id="retry"
                    type="number"
                    min="0"
                    max="10"
                    className="input"
                    value={formData.retry_count || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        retry_count: e.target.value ? parseInt(e.target.value) : undefined,
                      })
                    }
                    placeholder="Herdar do produto"
                  />
                </div>

                <div>
                  <label htmlFor="rate_minute" className="label text-gray-700 font-medium mb-1">
                    Limite de Débito (por minuto)
                  </label>
                  <input
                    id="rate_minute"
                    type="number"
                    min="1"
                    className="input"
                    value={formData.rate_limit_per_minute || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        rate_limit_per_minute: e.target.value
                          ? parseInt(e.target.value)
                          : undefined,
                      })
                    }
                    placeholder="Sem limite"
                  />
                </div>

                <div>
                  <label htmlFor="rate_day" className="label text-gray-700 font-medium mb-1">
                    Limite de Débito (por dia)
                  </label>
                  <input
                    id="rate_day"
                    type="number"
                    min="1"
                    className="input"
                    value={formData.rate_limit_per_day || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        rate_limit_per_day: e.target.value ? parseInt(e.target.value) : undefined,
                      })
                    }
                    placeholder="Sem limite"
                  />
                </div>

                <div className="col-span-2">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.enabled}
                      onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                      className="mr-2 rounded text-primary-600 focus:ring-primary-500 border-gray-300"
                    />
                    <span className="text-sm font-medium text-gray-700">Ativo</span>
                  </label>
                </div>
              </div>

              <div className="flex space-x-3 pt-4">
                <button type="button" onClick={closeModal} className="btn-secondary flex-1">
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="btn-primary flex-1"
                >
                  {createMutation.isPending || updateMutation.isPending
                    ? 'A guardar...'
                    : editingEndpoint
                      ? 'Atualizar Endpoint'
                      : 'Criar Endpoint'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
